import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { MapPin, Loader2, X } from 'lucide-react';
import { cn } from '../lib/utils';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/**
 * MapboxAddressAutocomplete - Address search with Mapbox Geocoding API
 * 
 * Features:
 * - Real-time address suggestions while typing
 * - Standardized address data extraction (street, city, state, zip, lat/lng)
 * - Keyboard navigation support
 * - Debounced API calls
 */
const MapboxAddressAutocomplete = ({ 
    value = '', 
    onChange, 
    onSelect, 
    placeholder = "Enter address (e.g., 123 Main St, Austin TX)",
    className,
    disabled = false,
    autoFocus = false,
    ...props 
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const inputRef = useRef(null);
    const suggestionsRef = useRef(null);
    const debounceRef = useRef(null);

    const isEnabled = MAPBOX_TOKEN && MAPBOX_TOKEN.length > 0;

    // Sync external value changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Handle click outside to close suggestions
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                suggestionsRef.current && 
                !suggestionsRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowSuggestions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch suggestions from Mapbox Geocoding API
    const fetchSuggestions = useCallback(async (query) => {
        if (!isEnabled || !query || query.length < 3) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            // Mapbox Geocoding API - search for addresses in USA
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?` +
                `access_token=${MAPBOX_TOKEN}&` +
                `country=us&` +
                `types=address&` +
                `limit=5&` +
                `autocomplete=true`
            );

            if (response.ok) {
                const data = await response.json();
                const features = data.features || [];
                setSuggestions(features);
                setShowSuggestions(features.length > 0);
            } else {
                console.error('Mapbox API error:', response.status);
                setSuggestions([]);
            }
        } catch (error) {
            console.error('Mapbox autocomplete error:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [isEnabled]);

    // Debounced search
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (inputValue && inputValue.length >= 3) {
            debounceRef.current = setTimeout(() => {
                fetchSuggestions(inputValue);
            }, 300);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [inputValue, fetchSuggestions]);

    // Parse Mapbox feature into standardized address
    const parseMapboxFeature = (feature) => {
        const context = feature.context || [];
        
        // Extract components from context
        const getContextValue = (type) => {
            const item = context.find(c => c.id.startsWith(type));
            return item ? item.text : '';
        };

        // Get address components
        const streetNumber = feature.address || '';
        const streetName = feature.text || '';
        const street = streetNumber ? `${streetNumber} ${streetName}` : streetName;
        
        const city = getContextValue('place') || getContextValue('locality') || '';
        const state = getContextValue('region');
        const stateCode = state ? (context.find(c => c.id.startsWith('region'))?.short_code?.replace('US-', '') || state) : '';
        const postalCode = getContextValue('postcode') || '';
        
        // Coordinates
        const [longitude, latitude] = feature.center || [null, null];

        return {
            street_address: street,
            city: city,
            state: stateCode || state,
            zip_code: postalCode,
            latitude: latitude,
            longitude: longitude,
            formatted_address: feature.place_name || `${street}, ${city}, ${stateCode} ${postalCode}`.trim(),
            raw_feature: feature
        };
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setHighlightedIndex(-1);
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleSelectSuggestion = (feature) => {
        const standardizedAddress = parseMapboxFeature(feature);
        
        // Update input with formatted address
        setInputValue(standardizedAddress.formatted_address);
        
        if (onChange) {
            onChange(standardizedAddress.formatted_address);
        }
        
        // Callback with full standardized data
        if (onSelect) {
            onSelect(standardizedAddress);
        }

        setShowSuggestions(false);
        setSuggestions([]);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev => 
                    prev < suggestions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    handleSelectSuggestion(suggestions[highlightedIndex]);
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                break;
            default:
                break;
        }
    };

    const handleClear = () => {
        setInputValue('');
        setSuggestions([]);
        setShowSuggestions(false);
        if (onChange) {
            onChange('');
        }
        inputRef.current?.focus();
    };

    // Fallback when Mapbox token is not configured
    if (!isEnabled) {
        return (
            <div className="relative w-full">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder={placeholder}
                    className={cn("pl-10 h-12 bg-accent border-border text-base", className)}
                    autoComplete="off"
                    disabled={disabled}
                    autoFocus={autoFocus}
                    {...props}
                />
            </div>
        );
    }

    return (
        <div className="relative w-full">
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <Input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                    placeholder={placeholder}
                    className={cn("pl-10 pr-10 h-12 bg-accent border-border text-base", className)}
                    autoComplete="off"
                    disabled={disabled}
                    autoFocus={autoFocus}
                    data-testid="mapbox-address-input"
                    {...props}
                />
                {loading && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                )}
                {!loading && inputValue && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground hover:text-foreground transition-colors"
                        tabIndex={-1}
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div 
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-72 overflow-auto"
                    data-testid="mapbox-suggestions"
                >
                    {suggestions.map((feature, index) => {
                        const parsed = parseMapboxFeature(feature);
                        return (
                            <button
                                key={feature.id || index}
                                type="button"
                                onClick={() => handleSelectSuggestion(feature)}
                                className={cn(
                                    "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors",
                                    "hover:bg-accent focus:bg-accent focus:outline-none",
                                    highlightedIndex === index && "bg-accent"
                                )}
                                data-testid={`mapbox-suggestion-${index}`}
                            >
                                <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                    <p className="font-medium text-foreground truncate">
                                        {parsed.street_address}
                                    </p>
                                    <p className="text-sm text-muted-foreground truncate">
                                        {parsed.city}{parsed.state && `, ${parsed.state}`} {parsed.zip_code}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                    
                    {/* Mapbox Attribution */}
                    <div className="px-4 py-2 border-t border-border bg-muted/30">
                        <p className="text-xs text-muted-foreground text-center">
                            Powered by Mapbox
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MapboxAddressAutocomplete;
